import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import {
  isDefaultListState, parseListSearch, serializeListSearch, validateListSearch,
  type ListConfig, type ListSearch, type ListState,
} from '@region13/shared-types'

/** A list screen's filter and sort state, and how to change it.
 *
 *  ⚠️ SCREENS TAKE THIS AS A PROP rather than calling a router hook, so their
 *  tests mount under QueryClient + Toast alone (`OpenGames` documents why that
 *  matters). The route component calls `useRouteListState` and passes it down;
 *  tests wrap the screen in `LocalListState`. */
export interface ListStateProps {
  state: ListState
  config: ListConfig
  set: (next: ListState) => void
  clear: () => void
  isDefault: boolean
}

function bind(config: ListConfig, state: ListState, write: (search: ListSearch) => void): ListStateProps {
  return {
    state, config,
    set: (next) => write(serializeListSearch(next, config)),
    clear: () => write({}),
    isDefault: isDefaultListState(state, config),
  }
}

/** URL-backed. Writes with `replace`, so filtering does not fill the back
 *  button's history.
 *
 *  ⚠️ MEMOIZED ON THE SERIALIZED SEARCH. A screen's memos key on `state` (the
 *  Open board's `view` callback and its options memo, route `open.tsx`), so a
 *  fresh parse on every render would recompute them on every render. The
 *  state and the bound setters keep their identity until the search changes. */
export function useRouteListState(config: ListConfig, keep: readonly string[] = []): ListStateProps {
  const raw = useSearch({ strict: false }) as Record<string, unknown>
  const navigate = useNavigate()
  const key = JSON.stringify(validateListSearch(raw))
  const state = useMemo(() => parseListSearch(JSON.parse(key) as ListSearch, config), [key, config])
  const keepKey = keep.join(',')
  return useMemo(() => bind(config, state, (next) => {
    // A filter change rewrites the whole search, so a param the list does not
    // own (the referee app's superadmin `viewTeam`) would be dropped by it;
    // `keep` names the ones that must survive. Read from the current search
    // at write time, not from this render's.
    const kept = keepKey ? keepKey.split(',') : []
    // The search shape is validated by each route's `validateSearch`; `as
    // never` sidesteps this generic call's strict per-route search typing.
    void navigate({
      to: '.',
      search: ((prev: Record<string, unknown>) => ({
        ...Object.fromEntries(kept.filter((k) => prev[k] !== undefined).map((k) => [k, prev[k]])),
        ...next,
      })) as never,
      replace: true,
    })
  }), [config, state, navigate, keepKey])
}

/** Component-state-backed, for tests. Memoized like `useRouteListState`. */
export function useLocalListState(config: ListConfig, initial: ListSearch = {}): ListStateProps {
  const [search, setSearch] = useState<ListSearch>(initial)
  return useMemo(() => bind(config, parseListSearch(search, config), setSearch), [config, search])
}

export function LocalListState({ config, initial, children }: { config: ListConfig; initial?: ListSearch; children: (ls: ListStateProps) => ReactNode }) {
  return <>{children(useLocalListState(config, initial))}</>
}
